import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { SettingsService, SettingEventType } from '@modules/settings/shared/services/settings.service';
import { BaseComponent } from '@shared/components/base.component';
import { HttpError } from '@shared/interfaces/error.interface';
import { Config } from '@shared/configs/config';
import { MANAGEABLE_EMPLOYEE_ROLES } from '@shared/auth/enums/app-role.enum';
import { Employee } from '@shared/models/employee.model';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { ConfirmationDiscardModalComponent } from '@shared/components/confirmation-discard-modal/confirmation-discard-modal.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-team-edit-employee',
  templateUrl: './team-edit-employee.component.html'
})
export class TeamEditEmployeeComponent extends BaseComponent implements OnInit {
  form: UntypedFormGroup;
  ButtonTypes = ButtonTypes;
  ButtonSize = ButtonSize;
  public readonly dialogRef = inject(MatDialogRef<TeamEditEmployeeComponent>);
  public readonly service = inject(SettingsService);
  private readonly changes = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);
  public readonly data = inject(MAT_DIALOG_DATA) as any;

  positionSelectOptions = [];

  constructor() {
    super();

    this.dialogRef.disableClose = true;

    this.dialogRef.backdropClick().subscribe(() => {
      this.cancel();
    });
  }

  ngOnInit(): void {
    this.createForm();
    this.setPositions();
    this.setEmployee(this.data['employee']);
  }

  ngAfterViewChecked() {
    this.changes.detectChanges();
  }

  setPositions() {
    MANAGEABLE_EMPLOYEE_ROLES.forEach((key) => {
      this.positionSelectOptions.push({
        value: key,
        name: this.t.instant('ROLES.' + key)
      });
    });
  }

  setEmployee(employee: Employee) {
    this.form.get('role').setValue(employee.role);
  }

  createForm() {
    this.form = new UntypedFormGroup({
      role: new UntypedFormControl('', [Validators.required]),
      id: new UntypedFormControl(this.data['employee'].id)
    });
  }

  cancel(): void {
    if (this.form.pristine) {
      this.closeModal();
    } else {
      let dialogRef = this.dialog.open(ConfirmationDiscardModalComponent, {
        width: Config.DEFAULT_MODAL_WIDTH,
        autoFocus: false
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        switch (confirmed) {
          case true:
            {
              this.closeModal();
            }
            break;
          default:
            break;
        }
      });
    }
  }

  submit() {
    Object.keys(this.form.controls).forEach((key: string) => {
      this.form.controls[key].markAsTouched();
      this.form.controls[key].updateValueAndValidity();
    });

    if (this.form.invalid || this.loading) {
      this.scrollToError();
      return;
    }
    this.loading = true;
    this.service
      .editEmployee(this.form.value)
      .subscribe({
        next: this.onSuccessEditEmployee.bind(this),
        error: this.onErrorEditEmployee.bind(this)
      })
      .add(() => {
        this.loading = false;
        this.changes.detectChanges();
      });
  }

  onSuccessEditEmployee() {
    this.s.success(this.t.instant('Settings.Team.employeeEdited'));
    this.service.emitter.emit({ type: SettingEventType.RELOAD_EMPLOYEES_LIST });
    this.form.reset();
    this.dialogRef.close();
  }

  onErrorEditEmployee(e: HttpError) {
    switch (e.messageCode) {
      default:
        this.s.error(this.t.instant('Settings.Team.Errors.errorEditEmployee'));
        break;
    }
  }

  closeModal() {
    this.form.reset();
    this.dialogRef.close();
  }
}
